import axios from 'axios';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { isTokenExpired } from './jwt';

/* ==========================================================================
   Coordenador de autenticação — um único lugar que sabe do token

   O painel tem dois clientes HTTP: o axios de `api.ts`, que atende todas as
   telas, e o `fetch` do stream do Vapt IA, que o axios não consegue fazer
   (SSE por POST precisa ler o corpo em pedaços, e o axios entrega tudo no
   fim). Se cada um guardasse a própria promessa de renovação, um token
   expirado com as duas chamadas em voo dispararia dois `POST /auth/refresh`;
   o segundo chegaria com o refresh token que o primeiro já rotacionou e
   derrubaria a sessão do dono no meio do trabalho.

   Por isso a promessa de renovação, a leitura do token corrente e a saída
   por falha moram aqui, e tanto o axios quanto o stream passam por este
   módulo. O comportamento continua sendo o que o axios já fazia — este
   arquivo é extração, não mudança.
   ========================================================================== */

const REFRESH_ENDPOINT = '/api/v1/auth/refresh';
const LOGIN_ROUTE = '/login';
const SUBSCRIPTION_BLOCKED_ROUTE = '/subscription-blocked';
const STORE_DEACTIVATED_ROUTE = '/store-deactivated';

/**
 * Margem, em segundos, usada por quem abre um stream longo. A chamada comum
 * só renova depois de levar 401 (margem zero); o stream renova antes de
 * abrir, porque um turno dura até 90 s e começar com um token no último
 * suspiro é pedir para cair no meio.
 */
const STREAM_TOKEN_BUFFER_SECONDS = 30;

/** Token de acesso vigente, ou `null` quando não há sessão. */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

/** Id do estabelecimento selecionado, ou `null` quando nenhum foi escolhido. */
export function getSelectedTenantId(): string | null {
  return useAuthStore.getState().selectedTenant?.id ?? null;
}

/**
 * Cabeçalhos de identificação que toda chamada autenticada do painel leva:
 * o Bearer e, quando há estabelecimento selecionado, o `X-Tenant-Id`.
 */
export function buildAuthHeaders(): Record<string, string> {
  const { accessToken, selectedTenant } = useAuthStore.getState();
  const headers: Record<string, string> = {};

  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (selectedTenant) headers['X-Tenant-Id'] = selectedTenant.id;

  return headers;
}

/** Encerra a sessão local e manda o usuário para a tela de entrada. */
export function signOut(): void {
  useAuthStore.getState().clearAuth();
  window.location.href = LOGIN_ROUTE;
}

/** Leva o usuário para a tela de assinatura bloqueada. */
export function redirectToSubscriptionBlocked(): void {
  window.location.href = SUBSCRIPTION_BLOCKED_ROUTE;
}

/**
 * Reconhece o 403 que a API manda quando a assinatura do estabelecimento não
 * está em dia. O marcador vai na extensão `errorCode` do ProblemDetails.
 */
export function isSubscriptionRequired(data: unknown): boolean {
  return hasErrorCode(data, 'subscription_required');
}

/** Leva o usuário para a tela de loja desativada. */
export function redirectToStoreDeactivated(): void {
  window.location.href = STORE_DEACTIVATED_ROUTE;
}

/**
 * Reconhece o 403 da loja desativada pela Vapt Menu. É um código próprio, e
 * não o da assinatura, porque pagar não reativa a loja.
 */
export function isTenantDeactivated(data: unknown): boolean {
  return hasErrorCode(data, 'tenant_deactivated');
}

function hasErrorCode(data: unknown, errorCode: string): boolean {
  return (
    !!data &&
    typeof data === 'object' &&
    'errorCode' in data &&
    (data as Record<string, unknown>).errorCode === errorCode
  );
}

/* ---- Renovação única em voo ---- */

let inFlightRefresh: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const { refreshToken, setTokens, clearAuth } = useAuthStore.getState();

  if (!refreshToken) {
    clearAuth();
    throw new Error('No refresh token');
  }

  try {
    // Chamada crua de propósito: passar pelo `api` cairia no próprio
    // interceptor de 401 e giraria em círculo.
    const response = await axios.post(REFRESH_ENDPOINT, { refreshToken });

    const { accessToken: newAccess, refreshToken: newRefresh } = response.data;
    setTokens(newAccess, newRefresh);
    return newAccess;
  } catch {
    signOut();
    throw new Error('Refresh failed');
  }
}

/**
 * Renova o token de acesso. Chamadas simultâneas compartilham a mesma
 * renovação: quem chegar enquanto uma está em voo espera a que já existe, em
 * vez de abrir outra. Rejeita quando a renovação falha — e nesse caso a
 * sessão já foi limpa e o redirecionamento para a entrada já aconteceu.
 */
export function refreshAccessToken(): Promise<string> {
  if (!inFlightRefresh) {
    inFlightRefresh = performRefresh().finally(() => {
      inFlightRefresh = null;
    });
  }

  return inFlightRefresh;
}

/** Há uma renovação acontecendo agora. Existe para os testes e o diagnóstico. */
export function isRefreshInFlight(): boolean {
  return inFlightRefresh !== null;
}

/**
 * Depois de um 401 numa chamada que saiu com `sentToken`, devolve o token com
 * que repeti-la, ou `null` quando repetir não adianta. É a regra do
 * interceptor do axios, para quem chama com `fetch`:
 *
 * - sem token na chamada (sem sessão) não há o que renovar;
 * - se outra chamada já renovou enquanto esta estava no ar, repete com o token
 *   novo, sem renovar de novo;
 * - só renova o token que o relógio daqui dá como vencido, e pela renovação
 *   única em voo — nunca um `POST /auth/refresh` paralelo.
 *
 * Quando a renovação falha, o coordenador já encerrou a sessão; aqui só se
 * devolve `null`.
 */
export async function renewAfterUnauthorized(sentToken: string | null): Promise<string | null> {
  if (!sentToken) return null;

  const current = getAccessToken();
  if (!current) return null;
  if (current !== sentToken) return current;
  if (!isTokenExpired(current, 0)) return null;

  try {
    return await refreshAccessToken();
  } catch {
    return null;
  }
}

/**
 * Devolve um token bom para abrir uma conexão longa, renovando antes se o
 * atual está vencido ou perto disso. Devolve `null` quando não há sessão ou
 * quando a renovação falhou — o chamador segue sem `Authorization` e a API
 * responde 401, que é o caminho já tratado.
 */
export async function ensureFreshAccessToken(
  bufferSeconds = STREAM_TOKEN_BUFFER_SECONDS,
): Promise<string | null> {
  const token = getAccessToken();
  if (!token) return null;
  if (!isTokenExpired(token, bufferSeconds)) return token;

  try {
    return await refreshAccessToken();
  } catch {
    return null;
  }
}
